declare global {
  interface Window {
    APP_SETTINGS: any;
  }
}

export const formDataToJPO = (formData: FormData) => {
  if (formData instanceof FormData) {
    const entries = formData.entries();

    return Array.from(entries).reduce((res, [key, value]) => {
      return { ...res, [key]: value };
    }, {});
  }

  return formData;
};

type Uniqueness<T> = (a: T, b: T) => boolean;

export const unique = <T>(list: T[] | undefined, expression: Uniqueness<T>): T[] => {
  const comparator = expression ?? ((a, b) => a === b);

  return (list ?? []).reduce<T[]>((res, item) => {
    const index = res.findIndex((elem) => comparator(elem, item));

    if (index < 0) res.push(item);

    return res;
  }, []);
};

export const isDefined = <T>(value: T | undefined | null): value is T => {
  return value !== null && value !== undefined;
};

export const isEmptyString = (value: any) => {
  return typeof value === "string" && value.trim() === "";
};

export const objectClean = <T extends AnyObject>(source: T) => {
  const cleanObject: [keyof T, unknown][] = Object.entries(source).reduce<[keyof T, unknown][]>((res, [key, value]) => {
    const valueIsDefined = isDefined(value) && !isEmptyString(value);

    if (!valueIsDefined) {
      return res;
    }

    if (Object.prototype.toString.call(value) === "[object Object]") {
      return [...res, [key, objectClean(value as AnyObject)]];
    }
    return [...res, [key, value]];
  }, []);

  return Object.fromEntries(cleanObject) as T;
};

export const numberWithPrecision = (n: number, precision = 1, removeTrailinZero = false) => {
  if (typeof n !== "number" || isNaN(n)) return "";

  let finalNum = n.toFixed(precision);

  if (removeTrailinZero) {
    finalNum = finalNum.replace(/.(0+)$/, "");
  }

  return finalNum;
};

export const humanReadableNumber = (n: number) => {
  const abs = Math.abs(n);

  if (isNaN(abs) || n === null) return "—";
  const normalizeNumber = (n: number) => numberWithPrecision(n, 1, true);

  let result;

  if (abs < 1e3) {
    result = normalizeNumber(n);
  } else if (abs >= 1e3 && abs < 1e6) {
    result = `${normalizeNumber(n / 1e3)}K`;
  } else if (abs >= 1e6 && abs < 1e9) {
    result = `${normalizeNumber(n / 1e6)}M`;
  } else {
    result = `${normalizeNumber(n / 1e9)}B`;
  }

  return result || null;
};

export const absoluteURL = (path = "") => {
  if (path.match(/^https?/) || path.match(/^\/\//)) {
    return path;
  }
  return [APP_SETTINGS.hostname.replace(/([/]+)$/, ""), path.replace(/^([/]+)/, "")].join("/");
};

export const removePrefix = (path: string) => {
  if (APP_SETTINGS.hostname) {
    const hostname = APP_SETTINGS.hostname;
    const prefix = new URL(hostname).pathname.replace(/([/]+)$/, "");

    return path.replace(new RegExp(`^${prefix}`), "");
  }

  return path || "/";
};

export const copyText = (text: string) => {
  const input = document.createElement("textarea");

  input.style.position = "fixed"; // don't mess up with scroll
  document.body.appendChild(input);

  input.value = text;
  input.focus();
  input.select();

  document.execCommand("copy");
  input.remove();
};

export const delay = (time = 0) => {
  return new Promise((resolve) => setTimeout(resolve, time));
};

export const clamp = (value: number, min: number, max: number) => {
  return Math.max(min, Math.min(value, max));
};

export const getLastTraceback = (traceback: string): string => {
  const lines = traceback.split("\n");
  let lastTraceIndex = -1;

  for (let i = lines.length - 1; i >= 0; i--) {
    if (lines[i].startsWith("  File")) {
      lastTraceIndex = i;
      break;
    }
  }

  return lastTraceIndex >= 0 ? lines.slice(lastTraceIndex).join("\n") : traceback;
};

/**
 * Open an external URL (docs, website). On the Capacitor native app this
 * uses the @capacitor/browser plugin so the page opens in a SYSTEM
 * in-app browser — a Chrome Custom Tab on Android, SFSafariViewController
 * on iOS — consistent on both, with proper status-bar chrome and a close
 * button. Without it, a plain ``target="_blank"`` navigated the app's own
 * WebView into the docs (rendering under the Android status bar — "runs
 * to the top") on Android and kicked out to Safari on iOS. On web it
 * falls back to a normal new tab.
 */
/** Original window.open, captured before the native shell patches it. */
let nativeWindowOpen: Window["open"] | null = null;

export const openExternalUrl = (url: string): void => {
  if (!url) return;
  const fallback = () => {
    const opener = nativeWindowOpen ?? window.open.bind(window);
    opener(url, "_blank", "noopener,noreferrer");
  };
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any)?.Capacitor;
    // ``Capacitor.Plugins`` is populated by @capacitor/core's JS
    // runtime (registerPlugin), which this bundle does NOT ship —
    // the injected native-bridge only READS that registry, so
    // ``Plugins.Browser`` is always undefined here and the old code
    // silently fell through to window.open on every device. The
    // bridge's low-level ``nativePromise(plugin, method, options)``
    // talks straight to the registered native plugin instead.
    if (cap?.nativePromise) {
      cap.nativePromise("Browser", "open", { url }).catch(fallback);
      return;
    }
    // Some bridge builds ship proxies on Plugins — keep as second try.
    const browser = cap?.Plugins?.Browser;
    if (browser?.open) {
      browser.open({ url }).catch(fallback);
      return;
    }
  } catch {
    /* fall through to a normal new tab */
  }
  fallback();
};

/** True inside the Capacitor native shell. */
export const isNativeApp = (): boolean =>
  typeof window !== "undefined" && typeof (window as { Capacitor?: unknown }).Capacitor !== "undefined";

/**
 * Native shell: route EVERY external link through the system in-app
 * browser. Any absolute http(s) link is external to the bundled app
 * (internal navigation uses relative SPA routes), so Docs / Website /
 * "learn more" links — wherever they live (footer, menu, settings) —
 * open in a Custom Tab (Android) / SFSafariViewController (iOS) instead
 * of navigating the app's own WebView (which rendered the page under the
 * Android status bar) or kicking out to the default browser (iOS).
 *
 * CAPTURE phase, not bubble: menus and dropdowns call
 * ``stopPropagation()`` in their bubble handlers to close themselves
 * (the sidebar Docs Menu.Item), which starved a document-level bubble
 * listener — the anchor's default ``target="_blank"`` then ran and the
 * WebView swallowed the navigation. Capture at document runs before ANY
 * component handler can stop the event; we preventDefault (killing the
 * navigation) but do NOT stopPropagation, so menus still see the click
 * and close normally.
 *
 * Also patches ``window.open``: programmatic opens never dispatch a
 * click, so absolute http(s) URLs are routed through the in-app browser
 * too (the original window.open is kept for openExternalUrl's fallback
 * and for non-http targets). No-op on web (normal links).
 */
export const installExternalLinkHandler = (): void => {
  if (typeof document === "undefined" || !isNativeApp()) return;
  const w = window as { __afri_extlink_installed?: boolean };
  if (w.__afri_extlink_installed) return;
  w.__afri_extlink_installed = true;
  document.addEventListener(
    "click",
    (e: MouseEvent) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!/^https?:\/\//i.test(href)) return; // relative / internal → SPA routing
      e.preventDefault();
      openExternalUrl(href);
    },
    true,
  );
  nativeWindowOpen = window.open.bind(window);
  window.open = ((url?: string | URL, target?: string, features?: string) => {
    const href = typeof url === "string" ? url : (url?.toString() ?? "");
    if (/^https?:\/\//i.test(href)) {
      openExternalUrl(href);
      return null;
    }
    return (nativeWindowOpen as Window["open"])(url as string, target, features);
  }) as Window["open"];
};
